import { useEffect, useMemo, useRef, useState } from "react";
import { useVirtualizer } from "@tanstack/react-virtual";
import { Circle, Star } from "lucide-react";
import type { AssetRecord } from "@focalfolio/domain-model";
import { useWorkspaceStore } from "../lib/store";
import { Thumbnail } from "./PreviewSurface";

function useSize<T extends HTMLElement>() {
  const ref = useRef<T>(null);
  const [size, setSize] = useState({ width: 900, height: 700 });
  useEffect(() => {
    const element = ref.current;
    if (!element) return;
    const observer = new ResizeObserver(([entry]) => {
      if (entry) setSize({ width: entry.contentRect.width, height: entry.contentRect.height });
    });
    observer.observe(element);
    return () => observer.disconnect();
  }, []);
  return { ref, size };
}

function AssetCard({ asset, selected, onOpen }: { asset: AssetRecord; selected: boolean; onOpen: () => void }) {
  const select = useWorkspaceStore((state) => state.select);
  return <button
    className={`asset-card ${selected ? "selected" : ""}`}
    type="button"
    onClick={(event) => select(asset.id, { additive: event.metaKey || event.ctrlKey, range: event.shiftKey })}
    onDoubleClick={onOpen}
    aria-pressed={selected}
    aria-label={`${asset.filename}, ${asset.rating} stars`}
  >
    <div className="asset-card-preview"><Thumbnail asset={asset} />{asset.label !== "none" ? <span className={`label-dot label-${asset.label}`} /> : null}</div>
    <div className="asset-card-copy"><strong title={asset.filename}>{asset.filename}</strong><span>{asset.kind.toUpperCase()} · {formatBytes(asset.size)}</span></div>
    <div className="asset-card-rating" aria-hidden="true">{Array.from({ length: 5 }, (_, index) => <Star key={index} size={11} fill={index < asset.rating ? "currentColor" : "none"} />)}</div>
  </button>;
}

function GridView({ assets }: { assets: AssetRecord[] }) {
  const { ref, size } = useSize<HTMLDivElement>();
  const selectedIds = useWorkspaceStore((state) => state.selectedIds);
  const set = useWorkspaceStore((state) => state.set);
  const columnWidth = 184;
  const columns = Math.max(1, Math.floor(Math.max(1, size.width - 26) / columnWidth));
  const rowCount = Math.ceil(assets.length / columns);
  const virtualizer = useVirtualizer({ count: rowCount, getScrollElement: () => ref.current, estimateSize: () => 226, overscan: 3 });
  return <div ref={ref} className="asset-scroll" role="grid" aria-label="Assets">
    <div className="virtual-space" style={{ height: virtualizer.getTotalSize() }}>
      {virtualizer.getVirtualItems().map((row) => {
        const start = row.index * columns;
        const items = assets.slice(start, start + columns);
        return <div key={row.key} className="virtual-grid-row" style={{ transform: `translateY(${row.start}px)`, gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))` }}>
          {items.map((asset) => <AssetCard key={asset.id} asset={asset} selected={selectedIds.includes(asset.id)} onOpen={() => set({ previewId: asset.id })} />)}
        </div>;
      })}
    </div>
  </div>;
}

function ListView({ assets }: { assets: AssetRecord[] }) {
  const parent = useRef<HTMLDivElement>(null);
  const selectedIds = useWorkspaceStore((state) => state.selectedIds);
  const select = useWorkspaceStore((state) => state.select);
  const set = useWorkspaceStore((state) => state.set);
  const virtualizer = useVirtualizer({ count: assets.length, getScrollElement: () => parent.current, estimateSize: () => 58, overscan: 10 });
  return <div ref={parent} className="asset-scroll list-scroll" role="grid" aria-label="Asset list">
    <div className="list-header"><span>Name</span><span>Type</span><span>Size</span><span>Modified</span><span>Rating</span></div>
    <div className="virtual-space" style={{ height: virtualizer.getTotalSize() }}>
      {virtualizer.getVirtualItems().map((row) => {
        const asset = assets[row.index];
        if (!asset) return null;
        const selected = selectedIds.includes(asset.id);
        return <button key={row.key} type="button" className={`asset-list-row ${selected ? "selected" : ""}`} style={{ transform: `translateY(${row.start}px)` }} onClick={(event) => select(asset.id, { additive: event.metaKey || event.ctrlKey, range: event.shiftKey })} onDoubleClick={() => set({ previewId: asset.id })}>
          <span className="list-name"><span className={`mini-kind kind-${asset.kind}`}>{asset.extension.toUpperCase() || "FILE"}</span><strong>{asset.filename}</strong></span>
          <span>{asset.kind}</span><span>{formatBytes(asset.size)}</span><span>{new Date(asset.lastModified).toLocaleDateString()}</span><span className="list-rating"><Star size={13} /> {asset.rating}</span>
        </button>;
      })}
    </div>
  </div>;
}

export function AssetBrowser() {
  const assets = useWorkspaceStore((state) => state.assets);
  const viewMode = useWorkspaceStore((state) => state.viewMode);
  const scanning = useWorkspaceStore((state) => state.scanning);
  const scan = useWorkspaceStore((state) => state.scanProgress);
  const primaryId = useWorkspaceStore((state) => state.primaryId);
  const select = useWorkspaceStore((state) => state.select);
  const set = useWorkspaceStore((state) => state.set);

  const indexById = useMemo(() => new Map(assets.map((asset, index) => [asset.id, index])), [assets]);
  useEffect(() => {
    const handler = (event: KeyboardEvent) => {
      if (event.defaultPrevented || (event.target as HTMLElement)?.closest("input, textarea, select, [contenteditable=true], [role=menu], .workspace-toolbar, .topbar, dialog")) return;
      const current = primaryId ? indexById.get(primaryId) ?? -1 : -1;
      if (["ArrowRight", "ArrowDown", "ArrowLeft", "ArrowUp"].includes(event.key)) {
        const delta = event.key === "ArrowRight" || event.key === "ArrowDown" ? 1 : -1;
        const next = assets[Math.max(0, Math.min(assets.length - 1, current + delta))];
        if (next) select(next.id, { additive: event.metaKey || event.ctrlKey, range: event.shiftKey });
        event.preventDefault();
      }
      if (event.key === "Enter" && primaryId) set({ previewId: primaryId });
      if (event.key === "Escape") useWorkspaceStore.getState().clearSelection();
    };
    addEventListener("keydown", handler);
    return () => removeEventListener("keydown", handler);
  }, [assets, indexById, primaryId, select, set]);

  if (scanning && assets.length === 0) return <div className="empty-state"><div className="scan-orbit"><Circle size={70} /><span>{scan.discovered.toLocaleString()}</span></div><h2>{scan.phase}</h2><p>Original files remain in place. Discovery and metadata extraction happen on this computer.</p></div>;
  if (assets.length === 0) return <div className="empty-state"><div className="empty-monogram">FF</div><h2>No matching assets</h2><p>Open a folder, choose another directory, or clear the active filters.</p></div>;
  return viewMode === "grid" ? <GridView assets={assets} /> : <ListView assets={assets} />;
}

export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  const units = ["KB", "MB", "GB", "TB"];
  let value = bytes / 1024;
  let unit = units[0];
  for (let index = 1; index < units.length && value >= 1024; index += 1) {
    value /= 1024;
    unit = units[index];
  }
  return `${value.toFixed(value >= 10 ? 1 : 2)} ${unit}`;
}
